import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import {
  ControlOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { PanelStatusGrid } from '@/features/dashboard/components/PanelStatusGrid';
import { SystemLogPanel } from '@/features/dashboard/components/SystemLogPanel';
import { getDashboardData } from '@/features/dashboard/services/dashboardApiService';
import { logger } from '@/shared/lib/logger';
import type { DashboardData } from '@/features/dashboard/types/dashboard.types';
import { GRID_CONFIG, RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { STATISTIC_COLORS } from '@/shared/constants/colors';
// UI定数
const UI_CONSTANTS = {
  AUTO_REFRESH_INTERVAL_MS: 30000, // 30秒
} as const;

export const DashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isMobile } = useResponsive();

  const loadDashboardData = useCallback(async (isInitialLoad: boolean = false) => {
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      const data = await getDashboardData();
      setDashboardData(data);
    } catch (error) {
      logger.captureError(error, 'DashboardPage.loadDashboardData');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回ロード
  useEffect(() => {
    loadDashboardData(true);
  }, [loadDashboardData]);

  // 自動更新（常に有効）
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadDashboardData(false);
    }, UI_CONSTANTS.AUTO_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [loadDashboardData]);

  if (isLoading) {
    return (
      <div>
        <div>読み込み中...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div>
        <div>データの読み込みに失敗しました</div>
      </div>
    );
  }

  // レスポンシブ設定
  const responsiveGutter = isMobile
    ? RESPONSIVE_STYLES.gutter.mobile
    : RESPONSIVE_STYLES.gutter.desktop;

  const statsColProps = isMobile
    ? GRID_CONFIG.statsCard.mobile
    : GRID_CONFIG.statsCard.desktop;

  const contentColProps = isMobile
    ? GRID_CONFIG.contentSection.mobile
    : GRID_CONFIG.contentSection.desktop;

  return (
    <div>
      {/* タイトルはHeaderに表示 */}

      <Row gutter={responsiveGutter} className="mb-6">
        <Col {...statsColProps}>
          <Card>
            <Statistic
              title="台数"
              value={dashboardData.statistics.totalPanelCount}
              prefix={<ControlOutlined />}
              valueStyle={{ color: STATISTIC_COLORS.TOTAL }}
            />
          </Card>
        </Col>

        <Col {...statsColProps}>
          <Card>
            <Statistic
              title="正常"
              value={dashboardData.statistics.activePanelCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: STATISTIC_COLORS.ACTIVE }}
            />
          </Card>
        </Col>

        <Col {...statsColProps}>
          <Card>
            <Statistic
              title="停止"
              value={dashboardData.statistics.stoppedPanelCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: STATISTIC_COLORS.INACTIVE }}
            />
          </Card>
        </Col>

        <Col {...statsColProps}>
          <Card>
            <Statistic
              title="警告"
              value={dashboardData.statistics.warningCount}
              prefix={<WarningOutlined />}
              valueStyle={{ color: STATISTIC_COLORS.WARNING }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={responsiveGutter}>
        <Col {...contentColProps}>
          <PanelStatusGrid gridData={dashboardData.panelStatusGrid} />
        </Col>

        <Col {...contentColProps}>
          <SystemLogPanel logData={dashboardData.systemLog} />
        </Col>
      </Row>
    </div>
  );
};